import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import { Box, Button, Divider, Typography } from "@mui/material";
import type { ReactNode } from "react";
import { mesfColors } from "@/setup/ag-grid/mesf-theme";
import { BANNER_CONTROL_HEIGHT } from "../../straightener-common/constants";
import type { StraightenerTrendBanner } from "../models/StraightenerTrend";

const labelSx = {
	fontSize: "0.7rem",
	fontWeight: 700,
	letterSpacing: "0.1em",
	color: mesfColors.text.secondary,
	textTransform: "uppercase",
	lineHeight: 1.1,
};

const valueSx = {
	fontSize: "0.95rem",
	fontWeight: 700,
	color: mesfColors.text.primary,
	lineHeight: 1.2,
};

export const StraightenerBanner = ({
	banner,
	onProductChange,
	recipeControl,
}: {
	banner: StraightenerTrendBanner | null;
	// Product Change button; absent for users who cannot change it.
	onProductChange?: () => void;
	// When provided, shown in the RECIPE slot instead of the plain recipe text:
	// this is the recipe picker.
	recipeControl?: ReactNode;
}) => {
	// The product is the one chosen with Product Change (the open straightener
	// campaign); null => nobody has chosen one yet.
	const selected = !!banner && banner.productSizeId != null;
	const hasRecipe = !!banner?.recipeId;
	// A product without recipes shows nothing in the recipe slot.
	const showRecipe = selected && (!!recipeControl || hasRecipe);

	return (
		<Box
			sx={{
				display: "flex",
				alignItems: "center",
				gap: 1.5,
				py: 0.5,
				px: 0.5,
				flexWrap: "wrap",
			}}
		>
			<Typography sx={labelSx}>Product</Typography>
			<Typography
				sx={
					selected ? valueSx : { ...valueSx, color: mesfColors.text.secondary }
				}
			>
				{selected
					? `(${banner?.productTypeName}) ${banner?.productName}`
					: "No product selected"}
			</Typography>
			{onProductChange ? (
				<Button
					variant="contained"
					color="primary"
					size="small"
					startIcon={<SwapHorizIcon />}
					onClick={onProductChange}
					sx={{ height: BANNER_CONTROL_HEIGHT }}
				>
					PRODUCT CHANGE
				</Button>
			) : null}

			{showRecipe ? (
				<>
					<Divider orientation="vertical" flexItem sx={{ my: 0.5 }} />
					<Typography sx={labelSx}>Recipe</Typography>
					{recipeControl ?? (
						<Typography sx={valueSx}>
							{`${banner?.name ?? ""}${banner?.crew ? ` · Crew ${banner.crew}` : ""}`}
							<Box
								component="span"
								sx={{ color: mesfColors.text.secondary, ml: 0.5 }}
							>
								{`· v${banner?.version}`}
							</Box>
						</Typography>
					)}
				</>
			) : null}
		</Box>
	);
};
